import { supabase } from './supabase.js';

/** Ligne profil public d'un participant (pour voir le profil d'un autre user). */
export async function fetchBarUserById(id) {
  if (!id) return null;
  const { data, error } = await supabase
    .from('bar_users')
    .select('id, username, avatar_url, featured_badge_code, is_guest, created_at')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[account] fetchBarUserById', error.message);
    return null;
  }
  return data ?? null;
}

/**
 * Agrégat cross-event d'un participant (bar_user_id). Toutes les tables sont en
 * RLS `select using(true)`, donc requêtes client directes — pas de RPC dédiée.
 * ponytail: pas de pagination ni de RPC ; si un habitué dépasse ~des milliers de
 * lignes, passer à un `get_bar_user_stats()` SQL agrégé.
 *
 * @param {string} barUserId
 * @returns {Promise<{
 *   totals: { events: number, suggestions: number, votesReceived: number, votesCast: number, badgeCount: number },
 *   badges: { code: string, count: number, lastAt: string|null }[]
 * }>}
 */
export async function fetchAccountOverview(barUserId) {
  const empty = {
    totals: { events: 0, suggestions: 0, votesReceived: 0, votesCast: 0, badgeCount: 0 },
    badges: [],
  };
  if (!barUserId) return empty;

  const [badgesRes, reqRes, voteRes] = await Promise.all([
    supabase
      .from('event_user_badges')
      .select('badge_code, event_id, created_at')
      .eq('bar_user_id', barUserId)
      .order('created_at', { ascending: false }),
    supabase.from('requests').select('event_id, votes').eq('bar_user_id', barUserId),
    supabase.from('votes').select('event_id').eq('bar_user_id', barUserId),
  ]);

  if (badgesRes.error) {
    // eslint-disable-next-line no-console
    console.warn('[account] badges', badgesRes.error.message);
  }

  const badgeRows = badgesRes.data ?? [];
  const reqRows = reqRes.data ?? [];
  const voteRows = voteRes.data ?? [];

  // Badges groupés par code (dédup, compte, dernière obtention).
  const byCode = new Map();
  for (const b of badgeRows) {
    const code = b.badge_code;
    if (!code) continue;
    const cur = byCode.get(code) ?? { code, count: 0, lastAt: null };
    cur.count += 1;
    if (!cur.lastAt || (b.created_at && b.created_at > cur.lastAt)) cur.lastAt = b.created_at ?? cur.lastAt;
    byCode.set(code, cur);
  }
  const badges = [...byCode.values()].sort(
    (a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? '')
  );

  const events = new Set();
  for (const r of reqRows) if (r.event_id) events.add(r.event_id);
  for (const v of voteRows) if (v.event_id) events.add(v.event_id);
  for (const b of badgeRows) if (b.event_id) events.add(b.event_id);

  const votesReceived = reqRows.reduce((s, r) => s + Math.max(0, Number(r.votes) || 0), 0);

  return {
    totals: {
      events: events.size,
      suggestions: reqRows.length,
      votesReceived,
      votesCast: voteRows.length,
      badgeCount: badges.length,
    },
    badges,
  };
}
